import { has, set, unset } from 'lodash'
import getCategorySingleton from './categories'
import { getPage, getValidRequestParams, getValidURLParams, getValidFilterParams } from './constants'

export function isObject (obj) {
  return typeof obj === 'object' && !Array.isArray(obj) && obj !== null
}

export function getPosition (options) {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, options)
  })
}

export function watchPosition (options) {
  return new Promise((resolve, reject) => {
    const id = navigator.geolocation.watchPosition((position) => {
      resolve({ id, position })
    }, reject, options)
  })
}

export function clearWatch (id) {
  return navigator.geolocation.clearWatch(id)
}

export function useSquareCard () {
  return document.documentElement.clientWidth < 576
}

export function fullScreenFilters () {
  return document.documentElement.clientWidth <= 576
}

/**
 * @param {boolean} enable
 */
export function lockBodyScrolling (enable) {
  if (enable) {
    document.body.style.overflowY = 'clip'
  } else if (document.body.style.overflowY) {
    document.body.style.removeProperty('overflow-y')
  }
}

export async function handleNearMeParam () {
  const urlParams = new URLSearchParams(window.location.search)
  if (urlParams.has('nearme')) {
    urlParams.delete('nearme')
    const position = await getPosition({ enableHighAccuracy: true })
    urlParams.set('latitude', position.coords.latitude)
    urlParams.set('longitude', position.coords.longitude)
    window.location.replace(`${window.location.origin}${window.location.pathname}?${urlParams.toString()}`)
  }
}

export function formatRecommendedAges (recommendedAges) {
  const lower = parseInt(recommendedAges.lower, 10)
  return recommendedAges.upper
    ? `De ${recommendedAges.lower} a ${recommendedAges.upper} años`
    : lower === 0
      ? 'Para todas las edades'
      : lower < 18
        ? `A partir de ${recommendedAges.lower} años`
        : 'Para padres'
}

// Take into account that some params may be objects and URLSearchParams
// does not encode them well
export function URLEncodeParams (params) {
  for (let [k, v] of Object.entries(params)) { //eslint-disable-line
    if (isObject(v)) params[k] = JSON.stringify(v)
  }
  return new URLSearchParams(params)
}

/**
 * Delete from params all those which are obtained from filters.
 *
 * @param {URLSearchParams} params
 */
export function deleteParamsFromFilters (collection, params) {
  const validFilterParams = getValidFilterParams(collection)
  Object.keys(validFilterParams).forEach(p => params.delete(p))
}

/**
 * Modify params in oldParams according to their values in newParams.
 * Do not modify those params which exist in oldParams but are not present
 * in newParams
 *
 * @param {URLSearchParams} oldParams
 * @param {URLSearchParams} newParams
 */
export function mergeURLSearchParams (oldParams, newParams) {
  newParams.forEach((value, key) => {
    oldParams.set(key, value)
  })
  return oldParams
}

export function updateURL (collection, filters) {
  const currentParams = new URLSearchParams(window.location.search)
  const filterParams = URLEncodeParams(filters)
  const updated = currentParams.toString() !== filterParams.toString()

  if (updated) {
    deleteParamsFromFilters(collection, currentParams)
    const mergedParams = mergeURLSearchParams(currentParams, filterParams)
    window.history.pushState(null, '', `${getPage(collection)}${Object.keys(filters).length > 0 ? `?${mergedParams.toString()}` : ''}`)
  }

  return updated
}

/**
 * @param {google.maps.Map} map
 */
export async function getRequestParamsFromURLParams (collection, map, options = {}) {
  const categorySingleton = await getCategorySingleton()
  const urlSearch = new URLSearchParams(window.location.search)
  const queryParams = {}
  const validURLParams = getValidURLParams(collection)

  for (const [key, value] of urlSearch) {
    if (has(validURLParams, key)) { set(queryParams, key, value) }
  }

  if (queryParams.search_category) {
    queryParams.search_category = categorySingleton.idFromSEO(queryParams.search_category)
  }

  if ((queryParams.latitude && queryParams.longitude) || options?.addMapBounds) {
    queryParams.latitude = undefined
    queryParams.longitude = undefined
    queryParams.bounds = JSON.stringify(map.getBounds().toJSON())
  }

  return queryParams
}

export function resetRequestParams (collection, requestParams) {
  const validRequestParams = getValidRequestParams(collection)

  for (const key in requestParams) {
    if (has(validRequestParams, key)) { unset(requestParams, key) }
  }

  requestParams.page = 1
}
